// Single source of truth for the expansion-signal taxonomy on the frontend.
// Mirrors backend src/middlewares/expansions/constants.js. Update both together.

// ─── Expansion color tokens ─────────────────────────────────────────────
// Single source for every hex used by the expansion feature. SCSS mirrors
// live in src/styles/variables.scss as $color-<name> — keep both in sync.
// Token names follow Tailwind palette conventions (familiar + accessibility-
// vetted) and are intentionally distinct from the brand $color-blue-* /
// $color-red-* / $color-yellow-* / $color-green-* tokens, which are reserved
// for non-feature chrome (buttons, links, toasts).
const T = {
  // Stage palette
  amber_500:   "#F59E0B",  // Exploring
  blue_500:    "#3B82F6",  // Investing (also Freshness.fresh)
  indigo_500:  "#6366F1",  // Landing
  emerald_500: "#10B981",  // Operating (also signal_strength.high, scope.expanding_within_presence)

  // Direction palette
  sky_300:     "#7dd3fc",  // Advancing (also STAGE_UP_RING_COLOR)
  sky_600:     "#0284c7",  // Advancing ink
  slate_400:   "#94a3b8",  // Steady (also signal_strength.low, scope.established_only, neutral fallback)
  slate_600:   "#475569",  // Steady ink
  red_400:     "#f87171",  // Retreating
  red_600:     "#dc2626",  // Retreating ink (also country-popup Hot badge ink)
  yellow_300:   "#fde047", // New direction — industry-standard "new/highlight"
                            //  yellow (Linear NEW badges, Figma plugin pills,
                            //  Vercel sticker accents). 15° hue gap from
                            //  Exploring's amber_500 (hue 38° vs 53°), so
                            //  they share the warm range but aren't the same
                            //  color. Direction and Stage filters live in
                            //  separate sections so the two warms don't sit
                            //  side-by-side in a single chip row.
  yellow_700:   "#a16207", // New ink — same-hue-family dark for pill text,
                            //  matching the convention used by Advancing
                            //  (sky_600), Retreating (red_600), Steady
                            //  (slate_600).

  // Freshness palette (slate-blue ramp — deliberately not the direction red/green
  // so a "Retreating + Fresh" row doesn't read as self-contradictory)
  blue_400:    "#60a5fa",  // Freshness.cooling
  blue_300:    "#93c5fd",  // Freshness.stale
  slate_300:   "#cbd5e1",  // Freshness.cold

  // Accent / cross-references
  orange_400:  "#fb923c",  // AHEAD_OF_PACE_COLOR
  brand_blue_6:"#013cf3",  // Bridge to SCSS $color-blue-6 — scope.entering_new_market reuses the brand blue
  premier_blue:"#0D33A8",  // Deep navy — in-palette (blue family); Premier badge (badge only)
};

// Helper: alpha-tinted rgba derived from a token. Kept inline (vs a million
// pre-baked alpha tokens) so callers can tune transparency per-context.
function rgba(hex, alpha) {
  const v = hex.replace("#", "");
  const r = parseInt(v.slice(0, 2), 16);
  const g = parseInt(v.slice(2, 4), 16);
  const b = parseInt(v.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export const EXPANSION_COLOR_TOKENS = T;

export const EXPANSION_STAGES = [
  {
    slug: "exploring",
    rank: 1,
    color: T.amber_500,
    color_bg: rgba(T.amber_500, 0.10),
    label_key: "expansion.stage.exploring",
    description_key: "expansion.stage.exploringDescription",
  },
  {
    slug: "investing",
    rank: 2,
    color: T.blue_500,
    color_bg: rgba(T.blue_500, 0.10),
    label_key: "expansion.stage.investing",
    description_key: "expansion.stage.investingDescription",
  },
  {
    slug: "landing",
    rank: 3,
    color: T.indigo_500,
    color_bg: rgba(T.indigo_500, 0.10),
    label_key: "expansion.stage.landing",
    description_key: "expansion.stage.landingDescription",
    is_call_now: true,
  },
  {
    slug: "operating",
    rank: 4,
    color: T.emerald_500,
    color_bg: rgba(T.emerald_500, 0.10),
    label_key: "expansion.stage.operating",
    description_key: "expansion.stage.operatingDescription",
  },
];

export const EXPANSION_STAGE_ESTABLISHED_SLUG = "established";

export const STAGE_BY_SLUG = EXPANSION_STAGES.reduce((acc, s) => {
  acc[s.slug] = s;
  return acc;
}, {});

export const STAGE_INDEX_BY_SLUG = EXPANSION_STAGES.reduce((acc, s, i) => {
  acc[s.slug] = i;
  return acc;
}, {});

// Cyan ring + ▲ for recent stage-up (<30d). Per taxonomy §5.1.
// Same token as Advancing direction's `color` — the on-map ring IS the
// visual representation of "advancing".
export const STAGE_UP_RING_COLOR = T.sky_300;

// `tier` mirrors backend EXPANSION_SIGNAL_TYPE[x].tier — keep both in sync.
// premier = hard-to-source / premium (badged in the timeline); standard = the
// unmarked default. This array is the single place that decides which of the
// 16 are premium.
// is_available: false = no live data yet → chip renders disabled. Mirror backend constants.js when a source ships.
export const EXPANSION_SIGNAL_TYPES = [
  { code: "AD",         primary_stage: "exploring", label_key: "expansion.signal.ad",        tier: "standard", is_available: true  },
  { code: "EVENT",      primary_stage: "exploring", label_key: "expansion.signal.event",     tier: "standard", is_available: false },
  { code: "NEWS",       primary_stage: "exploring", label_key: "expansion.signal.news",      tier: "standard", is_available: true  },
  { code: "DNS",        primary_stage: "investing", label_key: "expansion.signal.dns",       tier: "standard", is_available: false },
  { code: "TECH",       primary_stage: "investing", label_key: "expansion.signal.tech",      tier: "standard", is_available: false },
  { code: "INFRA",      primary_stage: "investing", label_key: "expansion.signal.infra",     tier: "premier", is_available: true  },
  { code: "IP",         primary_stage: "investing", label_key: "expansion.signal.ip",        tier: "premier", is_available: false },
  { code: "PARTNER",    primary_stage: "investing", label_key: "expansion.signal.partner",   tier: "standard",  is_available: true  },
  { code: "EVENT_PLUS", primary_stage: "investing", label_key: "expansion.signal.eventPlus", tier: "premier",  is_available: true },
  { code: "REG",        primary_stage: "landing",   label_key: "expansion.signal.reg",       tier: "premier",  is_available: false },
  { code: "ENTITY",     primary_stage: "landing",   label_key: "expansion.signal.entity",    tier: "premier",  is_available: false },
  { code: "EXEC",       primary_stage: "landing",   label_key: "expansion.signal.exec",      tier: "standard",  is_available: true  },
  { code: "OFFICE",     primary_stage: "landing",   label_key: "expansion.signal.office",    tier: "standard", is_available: true  },
  { code: "HIRE",       primary_stage: "landing",   label_key: "expansion.signal.hire",      tier: "standard", is_available: true  },
  { code: "SCALE",      primary_stage: "operating", label_key: "expansion.signal.scale",     tier: "standard", is_available: true  },
  { code: "PRODUCT",    primary_stage: "operating", label_key: "expansion.signal.product",   tier: "standard", is_available: true  },
];

export const SIGNAL_TYPE_BY_CODE = EXPANSION_SIGNAL_TYPES.reduce((acc, t) => {
  acc[t.code] = t;
  return acc;
}, {});

// ─── Premium signal tier ────────────────────────────────────────────────
// Presentation-only prominence for hard-to-source signals. STANDARD renders
// nothing; PREMIER renders the gold badge. Which TYPES are premier lives on
// EXPANSION_SIGNAL_TYPES[x].tier above. Mirrors backend EXPANSION_SIGNAL_TIER.
export const EXPANSION_SIGNAL_TIER = { STANDARD: "standard", PREMIER: "premier" };

// Self-contained palette, bound via inline :style like EXPANSION_DIRECTIONS /
// EXPANSION_FRESHNESS (no SCSS token needed). Gold ✦ on a soft gold pill.
export const EXPANSION_SIGNAL_TIERS = {
  premier: {
    slug: "premier",
    symbol: "✦",
    // Tinted badge: deep-navy text on a faint navy bg — stays in the blue-family
    // palette (in-palette, premium), not a foreign accent. Alpha applied in
    // buildTierChip(). No dot halo — the badge alone marks premium.
    color: T.premier_blue,  // deep navy #0D33A8
    tint_alpha: 0.10,       // badge background (matches chip bg weight)
    label_key: "expansion.signalTier.premier",
  },
};

// Mirrors backend EXPANSION_SIGNAL_SOURCE. The UI branches on MANUAL only — its
// subtype (e.g. "Exhibiting Booth") carries the real provenance, so the
// timeline shows that instead of the internal "Manual" ingestion label.
export const EXPANSION_SIGNAL_SOURCE = {
  ADS: "ads", JOBS: "jobs", NEWS: "news", LINKEDIN: "linkedin", MANUAL: "manual",
  LLM_EXTRACTION: "llm_extraction", SCRAPER: "scraper", PARTNER_API: "partner_api",
  REGISTRY_FEED: "registry_feed", DOMAIN_SEARCHES: "domain_searches",
};

// Pulse trend kinds + headline tones — mirror of backend constants.js PULSE_TREND_KIND / PULSE_HEADLINE_TONE
// (keep in sync). CSS classes `t-<kind>` / `is-<tone>` use these slugs.
export const PULSE_TREND_KIND = { NONE: "none", NEW: "new", UP: "up", DOWN: "down", FLAT: "flat" };
export const PULSE_HEADLINE_TONE = { MUTED: "muted", NEW: "new", UP: "up", STEADY: "steady" };

// Slug strings shared across the module. Avoids stringly-typed lookups
// scattered through echart-options.js / StageTiles / CompanyList / index.vue.
export const STAGE_SLUG = {
  EXPLORING: "exploring",
  INVESTING: "investing",
  LANDING:   "landing",
  OPERATING: "operating",
};

// Backend rows carry `<slug>_count` per stage (e.g. `landing_count`).
export function stageCount(row, slug) {
  return Number(row?.[`${slug}_count`]) || 0;
}

export const STAGE_SLUGS = EXPANSION_STAGES.map((s) => s.slug);

// Single source for direction palette — chip dots, row pills, map overlays, popup badges all read these.
export const EXPANSION_DIRECTIONS = {
  advancing:  { slug: "advancing",  symbol: "↑",
                color: T.sky_300, tint: rgba(T.sky_300, 0.18), line: rgba(T.sky_300, 0.45), ink: T.sky_600,
                label_key: "expansion.direction.advancing" },
  steady:     { slug: "steady",     symbol: "→",
                color: T.slate_400, tint: "#f5f7fa", line: "#e5e6ea", ink: T.slate_600,
                label_key: "expansion.direction.steady" },
  retreating: { slug: "retreating", symbol: "↓",
                color: T.red_400, tint: rgba(T.red_400, 0.10), line: rgba(T.red_400, 0.30), ink: T.red_600,
                label_key: "expansion.direction.retreating" },
  new:        { slug: "new",        symbol: "✦",
                color: T.yellow_300, tint: rgba(T.yellow_300, 0.20), line: rgba(T.yellow_300, 0.50), ink: T.yellow_700,
                label_key: "expansion.direction.new" },
};

// Recency, not sentiment — slate-blue palette avoids reading "fresh + retreating" as good.
export const EXPANSION_FRESHNESS = {
  fresh:   { slug: "fresh",   symbol: "●", color: T.blue_500,  max_days: 30,   label_key: "expansion.freshness.fresh" },
  cooling: { slug: "cooling", symbol: "■", color: T.blue_400,  max_days: 60,   label_key: "expansion.freshness.cooling" },
  stale:   { slug: "stale",   symbol: "■", color: T.blue_300,  max_days: 90,   label_key: "expansion.freshness.stale" },
  cold:    { slug: "cold",    symbol: "■", color: T.slate_300, max_days: null, label_key: "expansion.freshness.cold" },
};

export const MAP_MODE = {
  GLOBAL:   "global",
  COUNTRY:  "country",
  OUTGOING: "outgoing",
};

export const AHEAD_OF_PACE_COLOR = T.orange_400;

// Mirrors backend EXPANSION_SCOPE in stage_engine.js.
export const EXPANSION_SCOPES = {
  entering_new_market:        { slug: "entering_new_market",        color: T.brand_blue_6,  label_key: "expansion.scope.enteringNewMarket" },
  expanding_within_presence:  { slug: "expanding_within_presence",  color: T.emerald_500,   label_key: "expansion.scope.expandingWithinPresence" },
  established_only:           { slug: "established_only",           color: T.slate_400,     label_key: "expansion.scope.establishedOnly" },
};

// Buckets the backend's continuous 0–1 confidence into discrete chips.
export const SIGNAL_STRENGTH_BUCKETS = {
  high:   { slug: "high",   min: 0.7,  max: 1.01, color: T.emerald_500, label_key: "expansion.signalStrength.high" },
  medium: { slug: "medium", min: 0.4,  max: 0.7,  color: T.amber_500,   label_key: "expansion.signalStrength.medium" },
  low:    { slug: "low",    min: 0,    max: 0.4,  color: T.slate_400,   label_key: "expansion.signalStrength.low" },
};

export const RECENT_STAGE_UP_DAYS = 30;
export const NEW_EXPANSION_DAYS = 90;
export const MOVING_FAST_MAX_MONTHS = 6;

export const FOUNDED_YEAR_MIN = 1800;
export const FOUNDED_YEAR_MAX = 2100;

export const MAX_VISIBLE_MARKETS = 3;
// Below this many foreign markets there's nothing worth narrowing — hide the focus-filter chips.
export const EXPANSION_FILTER_MIN_MARKETS = 6;
export const MAX_GLOBAL_ARCS = 5;
export const MAX_ORIGIN_MARKERS = 8;
export const COMPANY_DOT_DENSITY_THRESHOLD = 30;

// Single user-controlled slider that scales markets/arcs/origin chips together.
export const MAP_DENSITY_MIN = 1;
export const MAP_DENSITY_MAX = 200;
export const MAP_DENSITY_STEP = 1;
export const DEFAULT_MAP_DENSITY_N = 15;

// `flows_size` request value the dashboard map sends to /dashboard at first paint.
// Sized for the DEFAULT density (not MAX) so the initial request fits a typical
// plan's max_record_display without forcing a 416-retry on every load.
export const MAP_FLOWS_REQUEST_SIZE = Math.ceil(DEFAULT_MAP_DENSITY_N * 6 * 1.1);

// Compute the flows_size needed for a given density. Users who drag the slider
// past DEFAULT can trigger a refetch with this larger value so arcs stay
// connected — without this, the map loses arcs past ~N=33 because backend only
// returned 99 flow rows at first paint.
export function flowsSizeForDensity(n) {
  const N = Math.max(MAP_DENSITY_MIN, Math.min(MAP_DENSITY_MAX, Number(n) || DEFAULT_MAP_DENSITY_N));
  return Math.ceil(N * 6 * 1.1);
}

// Fallback size used when the dashboard's preferred MAP_FLOWS_REQUEST_SIZE
// exceeds the user's plan cap (backend returns 416). Matches the backend's
// `TOP_FLOWS_LIMIT_DEFAULT` so any plan that accepts the standard caller-
// omit default will accept this retry. The map still renders, just with
// fewer arcs.
export const TOP_FLOWS_RETRY_SIZE = 50;

export function densityFromN(n) {
  const N = Math.max(
    MAP_DENSITY_MIN,
    Math.min(MAP_DENSITY_MAX, Number(n) || DEFAULT_MAP_DENSITY_N),
  );
  return {
    // All three scale with N so the slider value matches what the user sees.
    // Previously markets and arcs capped at 12, so slider=20 still rendered
    // 12 target chips — confusing mismatch between slider label and map.
    // ECharts' runtime hideOverlap is the safety net for chip pile-ups at
    // very dense slider values; we don't pre-cap at build time anymore.
    markets:     N,
    global_arcs: N,
    origins:     N,
  };
}

export function relativeDays(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.floor((Date.now() - then) / 86400000);
}

export function freshnessFromDays(days) {
  if (days == null) return "cold";
  if (days < EXPANSION_FRESHNESS.fresh.max_days) return "fresh";
  if (days < EXPANSION_FRESHNESS.cooling.max_days) return "cooling";
  if (days < EXPANSION_FRESHNESS.stale.max_days) return "stale";
  return "cold";
}
